import {
  useCallback,
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import type { MatchEvent } from "../../../store/useMatchLogStore";
import {
  fetchAllMatchEvents,
  fetchLoggerWithAuth,
  IS_E2E_TEST_MODE,
  LOGGER_API_URL,
} from "../../../lib/loggerApi";
import { normalizeMatchPayload } from "../utils";
import type { Match } from "../types";
import { parseTimestamp } from "./useMatchTimer";

/**
 * What a document implies its effective seconds are right now: the stored
 * baseline, plus elapsed time since ITS OWN period-start timestamp — but
 * ONLY when THIS document's own `clock_mode` says effective is actually
 * running. `useMatchTimer`'s timer loop only ever advances the effective
 * accumulator while `clock_mode === "EFFECTIVE"`; a document sitting in
 * INEFFECTIVE has a frozen effective baseline regardless of how stale its
 * `current_period_start_timestamp` is. Ungated, elapsed time gets added to
 * an accumulator that was never actually ticking (lead review: caught this
 * inflating a currently-frozen ineffective total by exactly the staleness
 * of the OTHER document's `last_mode_change_timestamp`, flipping the
 * comparison the wrong way and regressing the guard was built to prevent).
 * When frozen, the implied value is just the bare baseline — comparing two
 * frozen documents degrades to a plain `Math.max` over the stored numbers,
 * with no timestamp math involved at all.
 */
const impliedEffectiveNow = (doc: Match, nowMs: number): number => {
  const baseline =
    typeof doc.clock_seconds_at_period_start === "number"
      ? doc.clock_seconds_at_period_start
      : (doc.match_time_seconds ?? 0);
  if (doc.clock_mode !== "EFFECTIVE" || !doc.current_period_start_timestamp) {
    return baseline;
  }
  const elapsed = Math.max(
    0,
    (nowMs - parseTimestamp(doc.current_period_start_timestamp)) / 1000,
  );
  return baseline + elapsed;
};

/** Mirrors `impliedEffectiveNow` for the ineffective accumulator + `last_mode_change_timestamp`, gated on `clock_mode === "INEFFECTIVE"`. */
const impliedIneffectiveNow = (doc: Match, nowMs: number): number => {
  const baseline = doc.ineffective_time_seconds ?? 0;
  if (doc.clock_mode !== "INEFFECTIVE" || !doc.last_mode_change_timestamp) {
    return baseline;
  }
  const elapsed = Math.max(
    0,
    (nowMs - parseTimestamp(doc.last_mode_change_timestamp)) / 1000,
  );
  return baseline + elapsed;
};

/**
 * Floor a freshly-fetched match document's clock state at whatever the
 * client already knew, so a server document missing a dropped PATCH
 * (PMA-COCK-155 — see useMatchTimer.ts's `handleModeSwitch`) can never move
 * the derived clock backwards while a period is running.
 *
 * A clock baseline (`match_time_seconds`/`clock_seconds_at_period_start`,
 * or `ineffective_time_seconds`) and the timestamp it was taken from
 * (`current_period_start_timestamp`, or `last_mode_change_timestamp`) are
 * ONE unit, not two independent fields — flooring the number while taking
 * the timestamp from the OTHER document pairs a known-good baseline with
 * the wrong document's elapsed-time origin and produces a phantom FORWARD
 * jump, not a rollback guard (caught in lead review of the first version of
 * this fix). So each pair is compared and adopted as a whole: derive what
 * the previous document and the freshly-fetched document each imply RIGHT
 * NOW, and keep the greater pair completely intact — never mixing a number
 * from one with a timestamp from the other. "Right now" is computed with
 * the SAME mode-awareness `useMatchTimer`'s timer loop uses — elapsed time
 * only accrues onto whichever accumulator that document's own `clock_mode`
 * says is actually running; the frozen one is never inflated by an
 * unrelated timestamp's staleness (lead review, second pass).
 *
 * Only applies mid-period — `current_period_start_timestamp` present on
 * BOTH the previous and the freshly-fetched document. A genuine reset
 * explicitly nulls it (`useMatchTimer.handleGlobalClockReset` sends
 * `current_period_start_timestamp: null` alongside `allow_clock_rewind`),
 * and that deliberate rewind path must still work end to end, so this guard
 * steps aside for it rather than fighting it.
 */
export const applyClockNoRegressGuard = (
  prev: Match | null,
  next: Match,
  nowMs: number = Date.now(),
): Match => {
  if (!prev || prev.id !== next.id) return next;
  const isRunningPeriod =
    Boolean(prev.current_period_start_timestamp) &&
    Boolean(next.current_period_start_timestamp);
  if (!isRunningPeriod) return next;

  const result: Match = { ...next };

  if (impliedEffectiveNow(prev, nowMs) > impliedEffectiveNow(next, nowMs)) {
    result.match_time_seconds = prev.match_time_seconds;
    result.clock_seconds_at_period_start = prev.clock_seconds_at_period_start;
    result.current_period_start_timestamp = prev.current_period_start_timestamp;
  }

  if (impliedIneffectiveNow(prev, nowMs) > impliedIneffectiveNow(next, nowMs)) {
    result.ineffective_time_seconds = prev.ineffective_time_seconds;
    result.last_mode_change_timestamp = prev.last_mode_change_timestamp;
  }

  return result;
};

interface UseMatchDataParams {
  matchId?: string;
  isLoggerReady: boolean;
  t: (...args: any[]) => unknown;
  setLiveEvents: (events: MatchEvent[], sourceMatchId?: string) => void;
}

export interface UseMatchDataResult {
  match: Match | null;
  setMatch: Dispatch<SetStateAction<Match | null>>;
  loading: boolean;
  error: string | null;
  fetchMatch: () => Promise<void>;
  hydrateEvents: () => Promise<void>;
}

export const useMatchData = ({
  matchId,
  isLoggerReady,
  t,
  setLiveEvents,
}: UseMatchDataParams): UseMatchDataResult => {
  const [match, setMatch] = useState<Match | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMatch = useCallback(async () => {
    if (!matchId || !isLoggerReady) return;
    try {
      const response = await fetchLoggerWithAuth(
        `${LOGGER_API_URL}/matches/${matchId}`,
      );
      if (!response.ok) {
        const errorPayload = await response.text().catch(() => "");
        console.error("Failed to fetch match payload", {
          status: response.status,
          statusText: response.statusText,
          body: errorPayload,
        });
        throw new Error("Failed to fetch match");
      }
      const data = await response.json();
      const normalized = normalizeMatchPayload(data);
      setMatch((prev) => applyClockNoRegressGuard(prev, normalized));
      // #73: a successful fetch clears any stale error from a prior failed
      // one. Without this, `error` was a one-way ratchet — since fetchMatch
      // is re-invoked constantly through a live match's lifecycle (clock
      // start/stop, mode switches, period transitions, drift correction,
      // and CockpitTopSection's own resync button), a single transient
      // failure anywhere permanently locked the cockpit into
      // LoggerCockpit.tsx's `error || !match` full-screen guard for the
      // rest of the session, even after full recovery — including making
      // the resync button itself unable to ever recover the screen it sits
      // on, since a successful resync left the stale error standing.
      setError(null);
      // NOTE (PMA-COCK-027 fix): a share_token must only ever be minted by an
      // explicit admin click on "Compartir Analítica" (LoggerCockpit.tsx
      // handleShare -> runShareAnalyticsFlow -> POST /matches/:id/share).
      // This hook previously auto-POSTed a share token in the background
      // whenever one was missing, silently making the match's analytics
      // publicly viewable (share_token is the sole public-access gate, see
      // routers/public.py) before the admin ever chose to share, and it
      // raced ahead of the click handler so a manual click found
      // match.share_token already set and took the no-op "existing token"
      // path instead of firing POST — see PMA-COCK-027 AC2. Do not restore
      // this without also updating the contract.
    } catch (err: any) {
      setError(err.message || String(t("errorLoadingMatch")));
    } finally {
      setLoading(false);
    }
  }, [isLoggerReady, matchId, t]);

  useEffect(() => {
    fetchMatch();
  }, [fetchMatch]);

  const hydrateEvents = useCallback(async () => {
    if (!matchId) return;
    try {
      const events = await fetchAllMatchEvents(matchId);
      if (IS_E2E_TEST_MODE) {
        console.log("[hydrateEvents]", matchId, "items", events.length);
      }
      // PMA-COCK-218: sourceMatchId, same id domain matchId was fetched
      // with (see LiveMatch.tsx/useMatchLogStore.ts's setLiveEvents for
      // the full account of the bug this closes off on the sibling page).
      setLiveEvents(events, matchId);
    } catch (err) {
      console.error("Failed to hydrate match events", err);
    }
  }, [matchId, setLiveEvents]);

  return {
    match,
    setMatch,
    loading,
    error,
    fetchMatch,
    hydrateEvents,
  };
};
